// src/pages/Notifications/Notifications.tsx
import React, { useState, useEffect } from 'react';
import { toast } from "sonner";
import { tasksApi, TaskNotification } from '../../api/tasks';

interface NotificationsProps {
  setUnreadCount?: (count: number) => void;
}

function Notifications({ setUnreadCount }: NotificationsProps) {
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(true);

  // useEffect(() => {
  //   loadNotifications();
  // }, [unreadOnly]);


//   useEffect(() => {
//   const interval = setInterval(() => {
//     if (!document.hidden) {  
//       loadNotifications();
//     }
//   }, 10000);

//   return () => clearInterval(interval);
// }, [unreadOnly]);

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tasksApi.getNotifications(unreadOnly, 100);
      setNotifications(response.data);
      const unreadCount = response.data.filter((n: TaskNotification) => !n.is_read).length;
      if (setUnreadCount) {
        setUnreadCount(unreadCount);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(true);
    }
  };

  const handleMarkRead = async (notificationId: string) => {
    try {
      await tasksApi.markNotificationRead(notificationId);
      loadNotifications();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await tasksApi.markAllNotificationsRead();
      loadNotifications();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark all as read');
    }
  };

  const getKindIcon = (kind: string) => {
    switch (kind) {
      case 'ASSIGNED': return '📋';
      case 'MENTIONED': return '@';
      case 'STATUS': return '🔄';
      case 'BLOCKED': return '🚫';
      case 'COMMENT': return '💬';
      case 'COMPLETED': return '✅';
      case 'DUE_SOON': return '⏰';
      case 'OVERDUE': return '⚠️';
      default: return '📌';
    }
  };

  const getKindColor = (kind: string) => {
    switch (kind) {
      case 'ASSIGNED': return 'from-emerald-500 to-emerald-600';
      case 'MENTIONED': return 'from-blue-500 to-blue-600';
      case 'STATUS': return 'from-amber-500 to-amber-600';
      case 'BLOCKED': return 'from-rose-500 to-rose-600';
      case 'COMMENT': return 'from-indigo-500 to-indigo-600';
      case 'COMPLETED': return 'from-emerald-500 to-emerald-600';
      case 'DUE_SOON': return 'from-orange-500 to-orange-600';
      case 'OVERDUE': return 'from-red-500 to-red-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading notifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-600 font-medium">{error}</p>
          <button 
            onClick={loadNotifications}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="mb-8">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Notifications
            </h1>
            <p className="text-gray-500 mt-1">Stay updated with your task activities and mentions</p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setUnreadOnly(!unreadOnly)}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 shadow-sm ${
                unreadOnly 
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {unreadOnly ? 'Showing Unread Only' : 'Show All'}
            </button>

            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllRead}
                className="px-4 py-2 bg-white border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Mark All as Read
              </button>
            )}
          </div>
        </div>

        {notifications.length > 0 && (
          <div className="mt-6 flex gap-4 flex-wrap">
            <div className="bg-white rounded-xl px-5 py-3 shadow-md">
              <span className="text-sm text-gray-500">Total</span>
              <span className="ml-2 font-semibold text-gray-900">{notifications.length}</span>
            </div>
            {unreadCount > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl px-5 py-3 shadow-sm">
                <span className="text-sm text-blue-600">Unread</span>
                <span className="ml-2 font-semibold text-blue-700">{unreadCount}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications</h3>
          <p className="text-gray-500">
            {unreadOnly ? "You don't have any unread notifications" : "You're all caught up!"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleMarkRead(notification.id)}
              className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden transform hover:scale-[1.02] ${
                !notification.is_read ? 'border-l-4 border-blue-500' : 'opacity-90'
              }`}
            >
              <div className="p-5">
                <div className="flex gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getKindColor(notification.kind)} flex items-center justify-center text-white text-xl shadow-lg`}>
                    {getKindIcon(notification.kind)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-4 flex-wrap">
                      <div className="flex-1">
                        <h3 className={`font-semibold ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">{notification.body}</p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xs text-gray-400 whitespace-nowrap bg-gray-100 px-2 py-1 rounded-full">
                          {formatDate(notification.created_at)}
                        </span>
                        {!notification.is_read && (
                          <span className="px-2 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs rounded-full font-medium shadow-sm">
                            New
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {notifications.length > 0 && (
        <div className="mt-6 text-center">
          <button 
            onClick={loadNotifications}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mx-auto bg-white px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh notifications
          </button>
        </div>
      )}
    </div>
  );
}

export default Notifications;